import { useMemo } from "react";
import { useGetRecordSpansQuery } from "@/api/api";
import { bucketRecordSpans, RecordSpan } from "@/common/intervalBuckets";
import { safeName } from "@/common/recordTree";
import { HGInterval } from "@/common/types";
import { Badge } from "@/components/ui/badge";

/**
 * `getName` composes its parts around optional pieces, so a name whose era or
 * qualifier slot is empty comes back with a doubled or trailing space —
 * invisible in the picker's centred rows, obvious inside a chip.
 */
function chipLabel(interval: HGInterval): string {
  return safeName(interval).replace(/\s+/g, " ").trim();
}

interface RecordsEmptyProps {
  message: string;
  /**
   * Whether to look for somewhere else to send the user. False whenever the
   * emptiness is not about time — outside a collection, or while a search is
   * filtering the list.
   */
  enabled: boolean;
  userID?: string;
  /** The *resolved* collection id, never the slug the URL carries. */
  collectionID?: string;
  /** The same narrowing the list applies, so the counts describe that list. */
  collectionIDs?: string[];
  onNavigate: (interval: HGInterval) => void;
}

/**
 * The records list with nothing in it.
 *
 * On a collection page the message alone is a dead end: the records exist,
 * they are simply somewhere else on the time scale, and the only ways out —
 * "All records", re-clicking the open collection — widen to the whole
 * collection without saying where inside it to look (and re-clicking, once the
 * range already fits, flips to the notes tab instead). So the message is
 * followed by the intervals that do hold records, with their counts.
 */
export default function RecordsEmpty({
  message,
  enabled,
  userID,
  collectionID,
  collectionIDs,
  onNavigate,
}: RecordsEmptyProps) {
  const { data } = useGetRecordSpansQuery(
    { userID, collectionID, collectionIDs },
    { skip: !enabled || !userID || !collectionID },
  );

  const buckets = useMemo(() => {
    if (!data || data.truncated) return [];
    const spans: RecordSpan[] = data.spans.map((span) => ({
      start: BigInt(span.start),
      end: BigInt(span.end),
      count: span.count,
    }));
    return bucketRecordSpans(spans);
  }, [data]);

  return (
    <div className="flex grow flex-col items-center justify-center gap-4 px-4">
      <p className="text-sm text-muted-foreground">{message}</p>
      {buckets.length > 0 && (
        <div className="flex max-w-2xl flex-col items-center gap-2">
          {/* <p className="text-xs text-muted-foreground">Records elsewhere</p> */}
          <div className="flex flex-wrap items-center justify-center gap-2">
            {buckets.map((bucket) => (
              <Badge
                key={`${bucket.interval.it}:${bucket.interval.n}`}
                asChild
                variant="secondary"
                className="gap-1.5 px-3 py-1.5"
              >
                <button
                  type="button"
                  onClick={() => onNavigate(bucket.interval)}
                  className="cursor-pointer hover:bg-accent hover:text-accent-foreground"
                >
                  {chipLabel(bucket.interval)}
                  <span className="text-muted-foreground">{bucket.count}</span>
                </button>
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
